import React, { useState } from "react";
import { X, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { locTrigger } from "@/lib/api";

const todayISO = () => new Date().toISOString().slice(0, 10);

const NO_WORK_REASONS = [
  ["estimate_approval_pending", "Estimate Approval Pending"],
  ["customer_approval_pending", "Customer Approval Pending"],
  ["customer_instruction_pending", "Customer Instruction Pending"],
  ["insurance_approval_pending", "Insurance Approval Pending"],
  ["other", "Other"],
];

const inputCls =
  "w-full border border-[#e2e1eb] rounded-md px-2.5 py-1.5 text-[13px] focus:outline-none focus:border-yamaha-400";

const Field = ({ label, required, children }) => (
  <div>
    <label className="text-[11px] font-semibold uppercase tracking-wider text-[#747684] block mb-1">
      {label}{required && <span className="text-red-500 ml-0.5">*</span>}
    </label>
    {children}
  </div>
);

/** Shared "Customer Not Responding" → LOC trigger popup, used by both the
 *  PV Workbench and the AV Detail page. Creates a linked LOC Case without
 *  duplicating the source PV/AV record. */
export const LocTriggerModal = ({ source, sourceType, onClose, onSuccess, actorName, actorRole }) => {
  const [reason, setReason] = useState("vehicle_ready");
  const [readyDate, setReadyDate] = useState(todayISO());
  const [triggerDate, setTriggerDate] = useState(todayISO());
  const [reasonDetail, setReasonDetail] = useState("estimate_approval_pending");
  const [reasonOther, setReasonOther] = useState("");
  const [remarks, setRemarks] = useState("");
  const [address, setAddress] = useState("");
  const [vinNo, setVinNo] = useState("");
  const [saving, setSaving] = useState(false);

  const needsPvExtras = sourceType === "pv";

  const submit = async () => {
    if (reason === "vehicle_ready" && !readyDate) {
      toast.error("Vehicle Ready Date is required.");
      return;
    }
    if (reason === "no_work_started" && reasonDetail === "other" && !reasonOther.trim()) {
      toast.error("Please describe the 'Other' reason.");
      return;
    }
    setSaving(true);
    try {
      const body = {
        source_type: sourceType,
        source_id: source.id,
        loc_reason: reason,
        trigger_date: triggerDate,
        remarks: remarks || null,
        actor: actorName,
        actor_role: actorRole,
      };
      if (reason === "vehicle_ready") {
        body.ready_date = readyDate;
      } else {
        body.reason_detail = reasonDetail;
        body.reason_detail_other = reasonDetail === "other" ? reasonOther : null;
      }
      if (needsPvExtras) {
        body.address = address || null;
        body.vin_no = vinNo || null;
      }
      const created = await locTrigger(body);
      toast.success(`LOC Case ${created.loc_number} created.`);
      onSuccess?.(created);
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to create LOC case.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid="loc-trigger-modal">
      <div className="bg-white rounded-xl border border-[#e2e1eb] w-full max-w-md shadow-xl max-h-[90vh] overflow-y-auto">
        <div className="px-5 py-3 border-b border-[#e2e1eb] flex items-start gap-2 sticky top-0 bg-white">
          <div className="w-8 h-8 rounded-lg bg-amber-50 flex items-center justify-center">
            <AlertTriangle className="w-4 h-4 text-amber-600" />
          </div>
          <div className="flex-1">
            <h3 className="text-[15px] font-semibold text-[#1a1b22]">Customer Not Responding</h3>
            <div className="text-[11.5px] text-[#747684] mt-0.5">
              {source?.customer_name} · {source?.registration_number || source?.vin_no || ""}
            </div>
          </div>
          <button onClick={onClose} data-testid="loc-trigger-close-btn" className="text-[#747684] hover:text-[#1a1b22]">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-5 space-y-3">
          <Field label="LOC Reason" required>
            <select
              data-testid="loc-trigger-reason-select"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className={inputCls}
            >
              <option value="vehicle_ready">Vehicle Ready – Customer Not Taking Delivery</option>
              <option value="no_work_started">No Work Started – Customer Not Responding</option>
            </select>
          </Field>

          {reason === "vehicle_ready" ? (
            <Field label="Vehicle Ready Date" required>
              <input
                type="date"
                data-testid="loc-trigger-ready-date"
                value={readyDate}
                onChange={(e) => setReadyDate(e.target.value)}
                className={inputCls}
              />
            </Field>
          ) : (
            <>
              <Field label="Reason Work Not Started" required>
                <select
                  data-testid="loc-trigger-reason-detail-select"
                  value={reasonDetail}
                  onChange={(e) => setReasonDetail(e.target.value)}
                  className={inputCls}
                >
                  {NO_WORK_REASONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
              </Field>
              {reasonDetail === "other" && (
                <Field label="Describe Reason" required>
                  <input
                    data-testid="loc-trigger-reason-other-input"
                    value={reasonOther}
                    onChange={(e) => setReasonOther(e.target.value)}
                    className={inputCls}
                  />
                </Field>
              )}
              <Field label="LOC Trigger Date" required>
                <input
                  type="date"
                  data-testid="loc-trigger-date-input"
                  value={triggerDate}
                  onChange={(e) => setTriggerDate(e.target.value)}
                  className={inputCls}
                />
              </Field>
            </>
          )}

          {needsPvExtras && (
            <>
              <Field label="Customer Address (for LOC letter)">
                <textarea
                  data-testid="loc-trigger-address-input"
                  value={address}
                  onChange={(e) => setAddress(e.target.value)}
                  rows={2}
                  className={inputCls}
                />
              </Field>
              <Field label="Chassis / VIN No. (for LOC letter)">
                <input
                  data-testid="loc-trigger-vin-input"
                  value={vinNo}
                  onChange={(e) => setVinNo(e.target.value.toUpperCase())}
                  className={`${inputCls} font-mono`}
                />
              </Field>
            </>
          )}

          <Field label="Remarks">
            <textarea
              data-testid="loc-trigger-remarks-input"
              value={remarks}
              onChange={(e) => setRemarks(e.target.value)}
              rows={2}
              className={inputCls}
            />
          </Field>
        </div>

        <div className="px-5 py-3 border-t border-[#e2e1eb] flex justify-end gap-2 bg-[#fafafd] rounded-b-xl">
          <button
            onClick={onClose}
            data-testid="loc-trigger-cancel-btn"
            className="px-3 py-1.5 rounded-md text-[13px] font-medium text-[#444653] hover:bg-[#f0eff7]"
          >
            Cancel
          </button>
          <button
            onClick={submit}
            disabled={saving}
            data-testid="loc-trigger-submit-btn"
            className="px-4 py-1.5 rounded-md text-[13px] font-semibold text-white bg-amber-600 hover:bg-amber-700 disabled:opacity-60"
          >
            {saving ? "Creating…" : "Create LOC Case"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default LocTriggerModal;
